import { usePendingReservations } from '@/features/reservations/hooks/useReservations';
import {
  useConfirmReservation,
  useRejectReservation,
} from '@/features/reservations/hooks/useReservationMutations';
import EmptyState from '@/components/EmptyState';
import MonthCalendarPopover from '@/components/MonthCalendarPopover';
import ReservationActions from '@/features/reservations/components/ReservationActions';
import ReservationCard from '@/features/reservations/components/ReservationCard';
import { allowedActionsForReservation } from '@/features/reservations/hooks/allowedActionsForReservation';
import { useT } from '@/i18n';
import { fromApi, todayString } from '@/utils/datetime';
import { sectionRouter } from '@/utils/navigateMain';
import type { Reservation } from '@restaurant/api-client';
import {
  Block,
  Link as F7Link,
  List,
  NavLeft,
  NavRight,
  NavTitle,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useRef, useState } from 'react';

/** Compact label for the active date-filter chip. */
function formatDateChip(value: string): string {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function PendingQueuePage() {
  const t = useT();
  const { data: pending, isLoading, refetch } = usePendingReservations();
  const confirmMutation = useConfirmReservation();
  const rejectMutation = useRejectReservation();
  const [actionTarget, setActionTarget] = useState<Reservation | null>(null);
  const allowedActions = allowedActionsForReservation(actionTarget);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const popoverRef = useRef<any>(null);

  // Optional date filter - the queue lists PENDING across all dates; picking a
  // date narrows it to that day. null = show all. The calendar pill in the
  // navbar opens the month picker (same one the create/edit forms use).
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [calOpen, setCalOpen] = useState(false);
  const calBtnRef = useRef<HTMLButtonElement>(null);

  const handleTap = (reservation: Reservation) => {
    sectionRouter()?.navigate(`/reservations/${reservation.id}`);
  };

  // Tap the status badge → open the same Confirm/Decline popover the main list
  // uses. Previously this was a no-op, so on web (where the swipeout actions
  // aren't reachable) there was no way to action a pending reservation here.
  const handleBadgeTap = (reservation: Reservation, targetEl: HTMLElement) => {
    setActionTarget(reservation);
    requestAnimationFrame(() => {
      if (popoverRef.current?.el) f7.popover.open(popoverRef.current.el, targetEl);
    });
  };

  const handleConfirm = (reservation: Reservation) => {
    confirmMutation.mutate(
      { reservationId: reservation.id },
      {
        onSuccess: () =>
          f7.toast.show({ text: t.reservations.toast.confirmed, closeTimeout: 2000 }),
        onError: () => f7.dialog.alert(t.reservations.errors.confirm),
      },
    );
    f7.popover.close();
  };

  const handleReject = (reservation: Reservation) => {
    f7.dialog.prompt(
      t.reservations.prompts.rejectReason,
      t.reservations.prompts.rejectTitle,
      (reason) => {
        rejectMutation.mutate(
          { reservationId: reservation.id, reason },
          {
            onSuccess: () =>
              f7.toast.show({ text: t.reservations.toast.rejected, closeTimeout: 2000 }),
            onError: () => f7.dialog.alert(t.reservations.errors.reject),
          },
        );
      },
    );
    f7.popover.close();
  };

  // This queue only ever lists PENDING reservations, and ReservationActions
  // renders just Confirm/Decline for them - so the arrival/cancel callbacks it
  // requires are never reached here.
  const noop = () => {};

  const all = Array.isArray(pending) ? pending : [];
  // Sort soonest-first, then apply the optional single-day filter.
  const sorted = [...all].sort((a, b) => a.time.localeCompare(b.time));
  const items = dateFilter
    ? sorted.filter((r) => fromApi(r.time).toISODate() === dateFilter)
    : sorted;
  const count = items.length;

  return (
    <Page name="pending-queue" ptr onPtrRefresh={(done) => refetch().then(() => done())}>
      <Navbar>
        <NavLeft backLink={t.back} />
        <NavTitle>{t.reservations.pendingQueue.title(count)}</NavTitle>
        <NavRight>
          <button
            ref={calBtnRef}
            type="button"
            className={`wsp-filter-trigger${dateFilter ? ' is-active' : ''}`}
            aria-label={t.reservations.pendingQueue.filterByDate}
            onClick={() => setCalOpen(true)}
          >
            <i className="f7-icons">calendar</i>
          </button>
        </NavRight>
      </Navbar>

      {/* Active date-filter banner with a one-tap clear - mirrors the list
          view's status-filter banner. */}
      {dateFilter && (
        <Block
          strongIos
          inset
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 8,
          }}
        >
          <span>
            <i className="f7-icons" style={{ fontSize: 16, verticalAlign: '-3px', marginRight: 6 }}>
              calendar
            </i>
            {formatDateChip(dateFilter)}
          </span>
          <F7Link onClick={() => setDateFilter(null)}>{t.reservations.pendingQueue.showAll}</F7Link>
        </Block>
      )}

      {isLoading ? (
        <Block style={{ textAlign: 'center', paddingTop: 48 }}>
          <Preloader size={32} />
        </Block>
      ) : count > 0 ? (
        <>
          <List className="reservation-cards">
            {/* Invoked as a function (not <ReservationCard/>) so each child is a
                direct ListItem - see ReservationCard / FRONTEND_PRACTICES Rule 9. */}
            {items.map((reservation) =>
              ReservationCard({
                reservation,
                t,
                onTap: handleTap,
                onBadgeTap: handleBadgeTap,
                onSwipeConfirm: handleConfirm,
                onSwipeReject: handleReject,
                showDate: true,
              }),
            )}
          </List>

          <ReservationActions
            ref={popoverRef}
            reservation={actionTarget}
            allowedActions={allowedActions}
            onClose={() => f7.popover.close()}
            onConfirm={handleConfirm}
            onReactivate={noop}
            onReject={handleReject}
            onCancel={noop}
            onMarkArrived={noop}
            onMarkSeated={noop}
            onMarkInProgress={noop}
            onMarkCompleted={noop}
            onMarkNoShow={noop}
            onMarkNotStarted={noop}
            onReschedule={noop}
          />
        </>
      ) : (
        <EmptyState
          icon="checkmark_circle"
          iconColor="#34c759"
          title={
            dateFilter
              ? t.reservations.pendingQueue.emptyDateTitle
              : t.reservations.pendingQueue.emptyTitle
          }
          description={
            dateFilter
              ? t.reservations.pendingQueue.emptyDateDesc
              : t.reservations.pendingQueue.emptyDesc
          }
        />
      )}

      <MonthCalendarPopover
        open={calOpen}
        onClose={() => setCalOpen(false)}
        anchorEl={calBtnRef.current}
        selectedDate={dateFilter ?? todayString()}
        onSelect={(d) => {
          setDateFilter(d);
          setCalOpen(false);
        }}
      />
    </Page>
  );
}

export default PendingQueuePage;
